/**
 * Client-side thumbnail generation for Binder image attachments. The server
 * cannot decrypt to make thumbnails, so the client resizes and produces a small
 * WebP that is encrypted with the same content key as the full-res image.
 */

const THUMB_MAX_EDGE = 256;

export function computeThumbnailDimensions(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  if (width <= maxEdge && height <= maxEdge) return { width, height };
  const scale = maxEdge / Math.max(width, height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export async function makeThumbnail(file: Blob): Promise<Blob> {
  // imageOrientation:'from-image' applies EXIF rotation so portrait photos
  // are not sideways in the thumbnail.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const { width, height } = computeThumbnailDimensions(bitmap.width, bitmap.height, THUMB_MAX_EDGE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('makeThumbnail: 2D canvas context unavailable');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('makeThumbnail: toBlob returned null'))), 'image/webp', 0.8),
  );
}
